import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// A menu or flyout pinned under the control that opened it. Portalled with
// fixed coordinates, as DateRangePicker does, so a scrolling or clipped host
// cannot move or cut it. Escape and a click elsewhere close it; Escape also
// returns focus to the anchor.
export default function AnchoredPopover({ anchor, onClose, align = "start", className = "", label, role = "dialog", children, onMouseEnter, onMouseLeave }) {
    const pop = useRef(null);
    const [pos, setPos] = useState(null);
    const place = useCallback(() => {
        const node = anchor?.current;
        if (!node) return;
        const r = node.getBoundingClientRect();
        const width = pop.current?.offsetWidth ?? 0, height = pop.current?.offsetHeight ?? 0;
        const narrow = window.innerWidth < 500;
        let left = narrow ? 8 : align === "end" ? r.right - width : r.left;
        left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
        const below = r.bottom + 4;
        const top = below + height > window.innerHeight - 8 && r.top - height - 4 > 8 ? r.top - height - 4 : below;
        setPos({ left, top, maxHeight: Math.max(160, window.innerHeight - top - 12), width: narrow ? window.innerWidth - 16 : undefined });
    }, [anchor, align]);
    useLayoutEffect(() => { place(); }, [place]);
    useEffect(() => {
        const away = (event) => {
            if (anchor?.current?.contains(event.target) || pop.current?.contains(event.target)) return;
            onClose(false);
        };
        const key = (event) => { if (event.key === "Escape" && !event.defaultPrevented) { event.preventDefault(); onClose(true); } };
        const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(place);
        if (pop.current) observer?.observe(pop.current);
        window.addEventListener("mousedown", away);
        window.addEventListener("keydown", key);
        window.addEventListener("resize", place);
        window.addEventListener("scroll", place, true);
        return () => {
            observer?.disconnect();
            window.removeEventListener("mousedown", away);
            window.removeEventListener("keydown", key);
            window.removeEventListener("resize", place);
            window.removeEventListener("scroll", place, true);
        };
    }, [anchor, onClose, place]);
    return createPortal(<div ref={pop} role={role} aria-label={label} className={`anchored-popover rounded-[10px] ${className}`} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}
        style={pos ? { left: pos.left, top: pos.top, maxHeight: pos.maxHeight, ...(pos.width ? { width: pos.width } : {}) } : { left: 0, top: 0, visibility: "hidden" }}>{children}</div>, document.body);
}
